<template>
  <div class="flex items-center gap-1" contenteditable="false">
    <span class="flex-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{{ label }}</span>

    <button ref="moveUp" type="button" @click="move(-1)" :disabled="!canMoveUp"
            :title="$t('products.block_move_up', { block: label })" :aria-label="$t('products.block_move_up', { block: label })"
            class="p-1 rounded text-gray-500 hover:text-gray-700 disabled:opacity-30 focus:outline-none focus:ring-2 focus:ring-bpblue">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" />
      </svg>
    </button>
    <button ref="moveDown" type="button" @click="move(1)" :disabled="!canMoveDown"
            :title="$t('products.block_move_down', { block: label })" :aria-label="$t('products.block_move_down', { block: label })"
            class="p-1 rounded text-gray-500 hover:text-gray-700 disabled:opacity-30 focus:outline-none focus:ring-2 focus:ring-bpblue">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    <button type="button" @click="confirmingRemove = true"
            :title="$t('products.block_remove', { block: label })" :aria-label="$t('products.block_remove', { block: label })"
            class="p-1 rounded text-red-600 hover:text-red-400 focus:outline-none focus:ring-2 focus:ring-bpblue">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
      </svg>
    </button>

    <!-- Teleported so the modal isn't rendered (and clipped) inside the editor -->
    <teleport to="body">
      <modal-component v-if="confirmingRemove" :on-process="remove" :on-cancel="cancelRemove"
                       on-process-title="products.block_remove_confirm_action">
        <template v-slot:header>{{ $t('products.block_remove_confirm_title') }}</template>
        {{ $t('products.block_remove_confirm_text') }}
      </modal-component>
    </teleport>
  </div>
</template>

<script>
import { NodeSelection } from '@tiptap/pm/state'
import ModalComponent from '@/components/modals/ModalComponent'

/**
 * Move up / move down / remove (with confirmation) controls for an editor block node view.
 * Reusable by any block: pass the node view's editor, getPos and deleteNode.
 */
export default {
  name: 'BlockControls',

  components: {
    ModalComponent,
  },

  props: {
    editor: {
      type: Object,
      required: true,
    },
    getPos: {
      type: Function,
      required: true,
    },
    deleteNode: {
      type: Function,
      required: true,
    },
    // Block name, used in the buttons' accessible labels.
    label: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      canMoveUp: false,
      canMoveDown: false,
      confirmingRemove: false,
    }
  },

  mounted() {
    this.refreshMoveState()
    this.editor.on('transaction', this.refreshMoveState)
  },

  beforeUnmount() {
    this.editor.off('transaction', this.refreshMoveState)
  },

  methods: {
    // Position, parent and index of this block, or null while the node view isn't in the doc.
    blockPosition() {
      const pos = this.getPos()
      if (typeof pos !== 'number') return null
      const $pos = this.editor.state.doc.resolve(pos)
      return { pos, parent: $pos.parent, index: $pos.index() }
    },
    refreshMoveState() {
      const position = this.blockPosition()
      this.canMoveUp = !!position && position.index > 0
      this.canMoveDown = !!position && position.index < position.parent.childCount - 1
    },
    // Swaps the block with its previous (-1) or next (1) sibling in a single transaction.
    move(direction) {
      const position = this.blockPosition()
      if (!position) return

      const { pos, parent, index } = position
      const siblingIndex = index + direction
      if (siblingIndex < 0 || siblingIndex >= parent.childCount) return

      const block = parent.child(index)
      const sibling = parent.child(siblingIndex)
      const from = direction < 0 ? pos - sibling.nodeSize : pos
      const to = from + block.nodeSize + sibling.nodeSize
      const newPos = direction < 0 ? from : from + sibling.nodeSize

      const tr = this.editor.state.tr.replaceWith(from, to, direction < 0 ? [block, sibling] : [sibling, block])
      tr.setSelection(NodeSelection.create(tr.doc, newPos)).scrollIntoView()
      this.editor.view.dispatch(tr)

      // Keep keyboard focus on the button that was used, if this view survived the move.
      this.$nextTick(() => {
        const button = direction < 0 ? this.$refs.moveUp : this.$refs.moveDown
        if (button && !button.disabled) button.focus()
      })
    },
    cancelRemove() {
      this.confirmingRemove = false
    },
    remove() {
      this.confirmingRemove = false
      this.deleteNode()
    },
  },
}
</script>
